import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseApiConfig {
  args?: Record<string, string | number | boolean | null>;
  onSuccess?: (response: any) => unknown;
  onError?: (response: any) => unknown;
  type?: 'mutation' | 'query';
  returnOnly?: boolean;
}

interface TriggerFetchArgs {
  returnOnly?: boolean;
  args?: Record<string, string | number | boolean | null>;
}

export const useApi = (apiFunc: (...args: any) => any, config?: UseApiConfig) => {
  const [isLoading, setIsLoading] = useState(false);
  const initialized = useRef<boolean>(false);

  const triggerFetchDefaultOption = useMemo(
    () => ({
      returnOnly: false,
      args: config?.args,
    }),
    [config]
  );

  const triggerFetch = useCallback(
    async (option: TriggerFetchArgs = triggerFetchDefaultOption) => {
      const options = { ...triggerFetchDefaultOption, ...option };
      setIsLoading(true);
      const response = await apiFunc(options?.args);
      if (options.returnOnly) {
        setIsLoading(false);
        return response;
      }
      if (response?.status) {
        config?.onSuccess?.(response);
      } else {
        config?.onError?.(response);
      }
      setIsLoading(false);
      return response;
    },
    [apiFunc, config, triggerFetchDefaultOption]
  );

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      if (config?.type === 'mutation') return;
      triggerFetch();
    }
  }, []); // eslint-disable-line

  return {
    triggerFetch,
    isLoading,
    setIsLoading,
  };
};
